import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredPermissions?: string[]
  requiredRoles?: string[]
}

export function ProtectedRoute({
  children,
  requiredPermissions = [],
  requiredRoles = [],
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isLoading, hasPermission, hasRole, email, getAccessToken } = useAuth()
  const [checkingCheckout, setCheckingCheckout] = useState(true)

  // Check for pending checkout after authentication
  useEffect(() => {
    const handlePendingCheckout = async () => {
      if (!isAuthenticated || !email) {
        setCheckingCheckout(false)
        return
      }

      const pending = localStorage.getItem('pendingCheckout')
      if (!pending) {
        setCheckingCheckout(false)
        return
      }

      try {
        const { planId, billingCycle } = JSON.parse(pending)
        localStorage.removeItem('pendingCheckout')

        const baseUrl = import.meta.env.PROD
          ? 'https://marketing-ai-platform-production.up.railway.app'
          : import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:4000'
        const token = await getAccessToken()

        const response = await fetch(`${baseUrl}/api/v1/stripe/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            planId,
            billingCycle,
            customerEmail: email,
          }),
        })

        const result = await response.json()

        if (result.success && result.data?.url) {
          window.location.href = result.data.url
          return
        }
      } catch (error) {
        console.error('Error processing pending checkout:', error)
      }

      setCheckingCheckout(false)
    }

    handlePendingCheckout()
  }, [isAuthenticated, email, getAccessToken])

  // Show loading spinner while checking authentication or pending checkout
  if (isLoading || (isAuthenticated && checkingCheckout)) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <LoadingSpinner size="lg" text={checkingCheckout ? "Setting up your account..." : "Checking authentication..."} />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/auth/login"
        state={{ from: location }}
        replace
      />
    )
  }

  // Check required permissions
  if (requiredPermissions.length > 0) {
    const missingPermissions = requiredPermissions.filter(
      permission => !hasPermission(permission)
    )

    if (missingPermissions.length > 0) {
      return (
        <div className="min-h-screen flex items-center justify-center ">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-white mb-2">
              Access Denied
            </h1>
            <p className="text-white/70 mb-4">
              You don't have the required permissions to access this page.
            </p>
            <p className="text-sm text-white/60 mb-6">
              Required permissions: {missingPermissions.join(', ')}
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-primary-600 hover:text-primary-500"
            >
              &larr; Go back
            </button>
          </div>
        </div>
      )
    }
  }

  // Check required roles
  if (requiredRoles.length > 0) {
    const hasAnyRole = requiredRoles.some(role => hasRole(role))

    if (!hasAnyRole) {
      return (
        <div className="min-h-screen flex items-center justify-center ">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-white mb-2">
              Access Denied
            </h1>
            <p className="text-white/70 mb-4">
              Your role doesn't have access to this page.
            </p>
            <p className="text-sm text-white/60 mb-6">
              Required roles: {requiredRoles.join(', ')}
            </p>
            <button
              onClick={() => window.history.back()}
              className="text-primary-600 hover:text-primary-500"
            >
              &larr; Go back
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}
